/**
 * The clock time an INSTANT falls on, in Asia/Kolkata — `16:05` from
 * `2026-08-17T10:35:00.000Z`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT `toLocaleTimeString` OR `Intl.DateTimeFormat`.
 *
 * The same reason `istDayOfInstant` in `lib/dates.ts` is not: a runtime built
 * with `small-icu` silently falls back to a timezone or a locale it DOES know,
 * with no error attached — which is the exact failure CLAUDE.md flags for
 * `toLocaleString("en-IN")`. A wrong hour on a message is smaller than a wrong
 * digit grouping on an invoice, but it is wrong in the same silent way, and the
 * fix costs four lines.
 *
 * India has one timezone and has never observed daylight saving, so a fixed
 * +05:30 shift read out of the UTC parts is EXACT rather than an approximation.
 * CLAUDE.md: dates are always Asia/Kolkata, never machine-local — so nothing
 * here reads `getHours()`, which would report the browser's zone and be right
 * only by accident on a phone that happens to be set to IST.
 *
 * 24-HOUR, WITH LATIN DIGITS, IN BOTH LOCALES. §11.4 fixes the digits ("Numbers
 * stay Latin digits (1234) in both locales"), and a 24-hour clock needs no
 * am/pm string to translate — one fewer thing that can render in the wrong
 * language beside a Marathi sentence.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const IST_OFFSET_MINUTES = 5 * 60 + 30;

export function istTimeOfInstant(iso: string | null | undefined): string {
  if (typeof iso !== "string" || iso === "") return "";
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "";

  const shifted = new Date(instant.getTime() + IST_OFFSET_MINUTES * 60_000);
  const hours = String(shifted.getUTCHours()).padStart(2, "0");
  const minutes = String(shifted.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * The initials for an avatar — "SB" from "Shubham Banekar", "S" from "Sunil".
 *
 * TAKES CODE POINTS, NOT `charAt`. A Marathi display name ("शुभम") is outside
 * the BMP in places and `charAt(0)` would split a surrogate pair into half a
 * character, which renders as a replacement glyph in a circle on the owner's own
 * screen. Falls back to an empty string rather than a "?" — the name is already
 * printed above the bubble, so an empty circle is quieter than a question mark
 * that reads like an error.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== "");
  return words
    .slice(0, 2)
    .map((word) => [...word][0] ?? "")
    .join("");
}
